import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { PrimaryAnnotatedType, SecondaryAnnotatedType } from '@/components/annotated-type';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { Themes } from '@/components/microstory/theme';
import { StripPlot } from '@/components/strip-plot';
import { SunriseChart, SunriseChartData } from '@/components/sunrise-chart';
import { StoryMap } from '@/components/world-map/story';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type OnlineOpportunitiesChapter1Props = {
  data: OnlineOpportunitiesData;
};

export function OnlineOpportunitiesChapter1(props: OnlineOpportunitiesChapter1Props) {
  const { data } = props;

  const { i18n } = useLingui();

  const answers = [
    {
      id: 'everyDay',
      label: t({ id: 'online-opportunities.question-1.answer-1', message: 'Every day' }),
    },
    {
      id: 'mostDays',
      label: t({ id: 'online-opportunities.question-1.answer-2', message: 'Most days' }),
    },
    {
      id: 'occassionally',
      label: t({ id: 'online-opportunities.question-1.answer-3', message: 'Only occasionally' }),
    },
  ];

  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  const formatValue = useNumberFormatting('percent');

  const [sunriseChartData, setSunriseChartData] = useState<SunriseChartData>();

  const handleFirstStepsEnter = (evt: OnStepEnterExitArgs<SunriseChartData>) => {
    if (evt.data) {
      setSunriseChartData(evt.data);
    }
  };

  const [stripPlotHighlightedIds, setStripPlotHighlightedIds] = useState<string[]>();

  const handleSecondStepsEnter = (evt: OnStepEnterExitArgs<string[]>) => {
    setStripPlotHighlightedIds(evt.data);
  };

  const stripPlotData = (
    Object.entries(data.daily_internet_usage_bd) as [CountryIsoCode, AgeGroupBreakdown][]
  ).map(([countryCode, data]) => ({
    id: countryCode,
    label: countryIdToName(countryCode),
    ...data,
  }));

  return (
    <>
      <StoryQuestion
        question={t({
          id: 'online-opportunities.question-1.question',
          message:
            'How often have you been online in the last month, whether checking email, browsing, or going on social media?',
        })}
        answers={answers}
        onAnswer={handleAnswer}
        selectedAnswer={selectedAnswer}
        response={
          selectedAnswer?.id &&
          t({
            id: 'online-opportunities.question-1.response',
            message: select(selectedAnswer?.id, {
              everyDay: "You're with most young people",
              mostDays: "That's less than most young people",
              occassionally: "That's much less than most young people",
            }),
          })
        }
      />

      <ScrollRoot>
        <ScrollGraphic>
          <SunriseChart data={sunriseChartData} formatValue={formatValue} />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleFirstStepsEnter}>
          <ScrollStep
            data={[
              {
                label: t({
                  id: 'online-opportunities.chapter-1.sunrise-chart-label-1',
                  message: '% who go online every day',
                }),
                data: [
                  { label: youngerGeneration(i18n), value: data.daily_internet_usage.younger },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-1.text-1">
                On average, 77% of <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> go
                online every day, our survey found.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: t({
                  id: 'online-opportunities.chapter-1.sunrise-chart-label-1',
                  message: '% who go online every day',
                }),
                data: [
                  { label: youngerGeneration(i18n), value: data.daily_internet_usage.younger },
                ],
              },
              {
                data: [{ label: olderGeneration(i18n), value: data.daily_internet_usage.older }],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-1.text-2">
                That’s far more than{' '}
                <SecondaryAnnotatedType>those aged 40+,</SecondaryAnnotatedType> of whom only half
                are daily netizens.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <StoryMap theme={Themes.spinach} highlightedCountries={['JP', 'ZW']} />
        </ScrollGraphic>
        <ScrollSteps>
          <ScrollStep>
            <StoryText>
              <Trans id="online-opportunities.chapter-1.text-3">
                But not everyone is so connected. There is a large digital divide across the
                world&hellip;
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <StripPlot
            axisLabel={t({
              id: 'online-opportunities.chapter-1.strip-plot-label-1',
              message: '15–24 year-olds who say they go online every day',
            })}
            data={stripPlotData}
            formatValue={formatValue}
            getValue={(d) => d.younger}
            highlightedIds={stripPlotHighlightedIds}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleSecondStepsEnter}>
          <ScrollStep data={['JP']}>
            <StoryText>
              <Trans id="online-opportunities.chapter-1.text-4">
                In <PrimaryAnnotatedType>{countryIdToName('JP')},</PrimaryAnnotatedType>{' '}
                {formatValue(data.daily_internet_usage_bd.JP.younger)} of young people go online
                every day.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['ZW']}>
            <StoryText>
              <Trans id="online-opportunities.chapter-1.text-5">
                But in <PrimaryAnnotatedType>{countryIdToName('ZW')},</PrimaryAnnotatedType> only{' '}
                {formatValue(data.daily_internet_usage_bd.ZW.younger)} do.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
